import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, QrCode, MapPin, Copy, Check } from 'lucide-react';
import QRCode from 'react-qr-code';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { useRedemptions, useLevels, getPromoDiscountForLevel } from '../hooks/useData';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { getImageUrl } from '../utils/getImageUrl';
import { useUserGeolocation } from '../hooks/useUserGeolocation';
import { distanceLabelFromCoords } from '../utils/distance';
import type { Redemption } from '../hooks/useData';
import './Benefits.css';

const isExpired = (endDate?: string) => {
    if (!endDate) return false;
    const end = new Date(endDate).toISOString().slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return end < today;
};

const Benefits = () => {
    const navigate = useNavigate();
    const userFix = useUserGeolocation();
    const userPoint = userFix ? { lat: userFix.lat, lng: userFix.lng } : null;
    const { user } = useAuth();
    const { data: redemptions, loading } = useRedemptions(user?.id);
    const { data: levels } = useLevels();

    const totalVisits = redemptions?.filter(r => r.usedAt).length ?? 0;
    const sortedLevels = [...(levels || [])].sort((a, b) => a.minVisits - b.minVisits);
    const userLevelSlug = sortedLevels.filter((l) => totalVisits >= l.minVisits).pop()?.slug ?? 'bronze';
    const toast = useToast();
    const [showQrModal, setShowQrModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState<Redemption | null>(null);
    const [showValidatedSuccess, setShowValidatedSuccess] = useState(false);
    const [copied, setCopied] = useState(false);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const activeCoupons = redemptions.filter(r => {
        if (r.usedAt) return false;
        if (isExpired(r.promotion?.endDate)) return false;
        return true;
    });
    const partnerCount = new Set(activeCoupons.map((coupon) => coupon.establishmentId)).size;

    const qrData = selectedCoupon ? `TURISTA:${selectedCoupon.establishmentId}:${selectedCoupon.code}` : '';

    const openQrModal = (coupon: Redemption) => {
        setSelectedCoupon(coupon);
        setShowQrModal(true);
        setShowValidatedSuccess(false);
    };

    const closeQrModal = () => {
        setShowQrModal(false);
        setSelectedCoupon(null);
        setCopied(false);
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const copyCode = () => {
        if (selectedCoupon?.code) {
            navigator.clipboard.writeText(selectedCoupon.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    useEffect(() => {
        if (!showQrModal || !selectedCoupon?.id) return;
        const redemptionId = selectedCoupon.id;

        const checkUsed = async () => {
            try {
                const { data } = await supabase
                    .from('Redemption')
                    .select('usedAt')
                    .eq('id', redemptionId)
                    .single();
                if (data?.usedAt) {
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                    setShowQrModal(false);
                    setSelectedCoupon(null);
                    setCopied(false);
                    setShowValidatedSuccess(true);
                    toast.success('Cupom validado! Seu desconto foi aceito pelo estabelecimento.');
                }
            } catch {
                // falha silenciosa
            }
        };

        pollIntervalRef.current = setInterval(checkUsed, 1000);
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [showQrModal, selectedCoupon?.id, toast]);

    if (loading) return <div className="p-4 text-center">Carregando benefícios...</div>;

    return (
        <div className="benefits-page">
            <section className="benefits-hero">
                <div className="benefits-title-row">
                    <span className="benefits-title-icon">
                        <Gift size={20} />
                    </span>
                    <div>
                        <h1>Seus Beneficios</h1>
                        <p>{partnerCount} parceiros disponiveis</p>
                    </div>
                </div>

                <p className="benefits-subtitle">Toque em um cupom para mostrar o QR Code ao lojista</p>
            </section>

            <section className="benefits-list">
                {activeCoupons.length === 0 ? (
                    <div className="benefits-empty">
                        <p>Voce nao tem beneficios ativos no momento.</p>
                        <button type="button" className="benefits-empty-btn" onClick={() => navigate('/')}>
                            Explorar locais
                        </button>
                    </div>
                ) : (
                    activeCoupons.map(coupon => (
                        <article key={coupon.id} className="benefit-card">
                            <div className="benefit-image-wrap">
                                <img
                                    src={getImageUrl(coupon.establishment?.image || '')}
                                    alt=""
                                    className="benefit-image"
                                    loading="lazy"
                                />
                                <span className="benefit-discount">
                                    {coupon.promotion ? getPromoDiscountForLevel(coupon.promotion, userLevelSlug) : 'Oferta especial'}
                                </span>
                            </div>

                            <div className="benefit-content">
                                <div className="benefit-meta-row">
                                    <span className="benefit-category">
                                        {coupon.establishment?.category?.label || 'Parceiro'}
                                    </span>
                                    <span className="benefit-distance">
                                        <MapPin size={15} strokeWidth={2} aria-hidden />
                                        {distanceLabelFromCoords(
                                            userPoint,
                                            coupon.establishment?.latitude,
                                            coupon.establishment?.longitude,
                                            coupon.establishment?.distance,
                                        )}
                                    </span>
                                </div>

                                <h3 className="benefit-title">{coupon.establishment?.name || 'Estabelecimento'}</h3>

                                {coupon.promotion?.title ? (
                                    <p className="benefit-promo-line">{coupon.promotion.title}</p>
                                ) : null}

                                <div className="benefit-description preserve-text-layout">
                                    {coupon.establishment?.description?.trim()
                                        ? coupon.establishment.description
                                        : 'Benefício válido para consumo no local.'}
                                </div>

                                <button type="button" className="benefit-redeem-btn" onClick={() => openQrModal(coupon)}>
                                    <QrCode size={20} strokeWidth={2} aria-hidden />
                                    <span>Mostrar QR Code</span>
                                </button>
                            </div>
                        </article>
                    ))
                )}
            </section>

            {showQrModal && selectedCoupon && (
                <div className="modal-overlay" onClick={closeQrModal}>
                    <div className="modal-content redemption-modal" onClick={e => e.stopPropagation()}>
                        <h3>🎉 Cupom Resgatado</h3>
                        <p className="promo-modal-distance">
                            <MapPin size={14} strokeWidth={2} aria-hidden />
                            {distanceLabelFromCoords(
                                userPoint,
                                selectedCoupon.establishment?.latitude,
                                selectedCoupon.establishment?.longitude,
                                selectedCoupon.establishment?.distance,
                            )}
                        </p>
                        <p className="promo-modal-title">{selectedCoupon.promotion?.title || 'Benefício'}</p>
                        <p className="promo-modal-discount">{selectedCoupon.promotion ? getPromoDiscountForLevel(selectedCoupon.promotion, userLevelSlug) : 'Oferta especial'}</p>
                        <div className="qr-container">
                            <QRCode value={qrData} size={150} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} />
                        </div>
                        <div className="code-container">
                            <p className="code-label">Código alternativo:</p>
                            <div className="code-box" onClick={copyCode}>
                                <span className="code-text">{selectedCoupon.code}</span>
                                {copied ? <Check size={18} color="#22c55e" /> : <Copy size={18} />}
                            </div>
                            <p className="code-hint">Clique para copiar</p>
                        </div>
                        <p className="modal-instruction">Mostre este QR Code ou informe o código ao lojista. O valor da compra é informado por ele na validação.</p>
                        <Button variant="secondary" onClick={closeQrModal} fullWidth>Fechar</Button>
                    </div>
                </div>
            )}

            {showValidatedSuccess && (
                <div className="modal-overlay" onClick={() => setShowValidatedSuccess(false)}>
                    <div className="modal-content redemption-modal validated-success" onClick={e => e.stopPropagation()}>
                        <h3>✅ Promoção Aceita!</h3>
                        <p className="promo-modal-title">Seu desconto foi validado pelo estabelecimento.</p>
                        <Button variant="primary" onClick={() => setShowValidatedSuccess(false)} fullWidth>OK</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Benefits;
